import type { Api } from "../api/client.js";
import type { KycConfirmed, SupportContact } from "../api/types.js";
import { openSheet, passkeySheet, supportSheet, type SheetHandle } from "../ui/sheet.js";
import { CardSigner } from "./card-signer.js";
import { KycFlow, type KycSigner } from "./kyc-flow.js";
import { whenFocused } from "./passkey.js";

/**
 * DCTA-176 — the identity-check refresh as one layer of the app (main.ts mounts it over the chat, Home or Me): the flow
 * (`kyc-flow.ts`), the passkey signer for its one card, and the two sheets it can raise over itself — Talk to us (the
 * bank's number, `GET /v1/support`) and "passkeys aren't available here".
 */
export interface KycScreenDeps {
  readonly api: Api;
  /** The column the sheets rise in (`#app`). */
  readonly host: HTMLElement;
  readonly customerId: string;
  /** The check's status as the session knows it. */
  readonly status: string;
  readonly reduced: () => boolean;
  /** The bank's clock (Done's "Checked", when the receipt carries no time). */
  readonly now: () => string | undefined;
  readonly onClose: (kyc: KycConfirmed | undefined) => void;
  readonly onSignedOut: () => void;
  /** A test's signer; the app's is the CardSigner on this phone's passkeys. */
  readonly signer?: (bundleId: string) => KycSigner;
  /** DCTA-199 ruling: the declaration's card, drawn — to be read aloud ("Check and sign."). */
  readonly onCard?: ((bundleId: string) => void) | undefined;
}

export class KycScreen {
  readonly flow: KycFlow;
  readonly #d: KycScreenDeps;
  #sheet: SheetHandle | undefined;
  #support: SupportContact | undefined;

  constructor(deps: KycScreenDeps) {
    this.#d = deps;
    this.flow = new KycFlow({
      api: deps.api,
      status: deps.status,
      signer:
        deps.signer ??
        ((bundleId) =>
          new CardSigner(
            {
              api: deps.api,
              customerId: deps.customerId,
              credentials: navigator.credentials,
              storage: localStorage,
              focus: () => whenFocused(document, window, 1000),
              now: () => Date.now(),
            },
            bundleId,
          )),
      onTalk: () => void this.#talk(),
      onClose: (kyc) => {
        void this.#closeSheet();
        deps.onClose(kyc);
      },
      onSignedOut: deps.onSignedOut,
      onUnsupported: () => this.#unsupported(),
      now: deps.now,
      onCard: deps.onCard,
    });
  }

  get parts(): readonly HTMLElement[] {
    return this.flow.view.parts;
  }

  /** A passkey sheet is up: the system's Back must not take the flow away. */
  get busy(): boolean {
    return this.flow.busy;
  }

  /** Taken away from outside: the live card is cancelled, a sheet over it goes. */
  close(): void {
    this.flow.close();
    void this.#closeSheet();
  }

  /** Talk to us: the bank's number and hours, read once (the operator's config — the one source a scam cannot substitute). */
  async #talk(): Promise<void> {
    if (this.#sheet !== undefined) return;
    try {
      this.#support ??= await this.#d.api.support();
    } catch {
      return;
    }
    this.#sheet = openSheet(this.#d.host, supportSheet(this.#support, () => void this.#closeSheet()), {
      behind: this.parts,
      reduced: this.#d.reduced(),
      animate: false,
      onDismiss: () => void this.#closeSheet(),
    });
  }

  #unsupported(): void {
    if (this.#sheet !== undefined) return;
    this.#sheet = openSheet(this.#d.host, passkeySheet("unsupported", { primary: { label: "OK", onSelect: () => void this.#closeSheet() } }).sheet, {
      behind: this.parts,
      reduced: this.#d.reduced(),
      animate: false,
      onDismiss: () => void this.#closeSheet(),
    });
  }

  async #closeSheet(): Promise<void> {
    const sheet = this.#sheet;
    this.#sheet = undefined;
    await sheet?.close();
  }
}
